import React, { useState, useRef } from 'react';
import { RatingBar } from './StarRating';
import ShareButton from './ShareableCard';
import { flagReview, absoluteVoiceUrl } from './realTalkApi';

const CATEGORY_LABELS = { academics: 'Academics', placements: 'Placements', campus_life: 'Campus Life', faculty: 'Faculty', value_for_money: 'Value for Money' };

function getTimeAgo(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = (now - date) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export default function VerdictCard({ review }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [flagged, setFlagged] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const audioRef = useRef(null);

  const displayText = review.ai_verdict || review.written_review || '';
  const shouldTruncate = displayText.length > 180;
  const truncated = shouldTruncate && !expanded ? displayText.substring(0, 180) + '...' : displayText;

  const handlePlayVoice = () => {
    if (!audioRef.current) return;
    if (isPlaying) { audioRef.current.pause(); audioRef.current.currentTime = 0; setIsPlaying(false); }
    else { audioRef.current.play(); setIsPlaying(true); }
  };

  const handleFlag = async () => {
    if (flagged) return;
    try { await flagReview(review.review_id); setFlagged(true); } catch { /* silent */ }
  };

  const overall = review.overall_rating || (Object.values(review.ratings || {}).reduce((a, b) => a + b, 0) / 5).toFixed(1);
  const overallColor = overall >= 4 ? '#22c55e' : overall >= 3 ? '#D4A24C' : overall >= 2 ? '#f97316' : '#ef4444';
  const reviewerDisplay = review.is_anonymous ? 'Anonymous Student' : review.reviewer_name || 'A Student';
  const timeAgo = getTimeAgo(review.created_at);

  return (
    <article className="rt-verdict-card" data-testid={`rt-verdict-${review.review_id}`}>
      <div className="rt-card-header">
        <div className="rt-card-college-info">
          <h3 className="rt-card-college">{review.college_name}{review.verified && (<span className="rt-verified-badge" title="Verified Student" data-testid="rt-verified-badge">✓ Verified</span>)}</h3>
          <span className="rt-card-meta">{review.course} · {review.year}{review.city ? ` · ${review.city}` : ''}</span>
        </div>
        <div className="rt-card-overall" style={{ borderColor: overallColor }}>
          <span className="rt-card-overall-num" style={{ color: overallColor }}>{overall}</span>
          <span className="rt-card-overall-label">/5</span>
        </div>
      </div>
      <div className="rt-card-ratings">
        {Object.entries(CATEGORY_LABELS).map(([key, label]) => (<RatingBar key={key} label={label} value={(review.ratings || {})[key] || 0} />))}
      </div>
      <div className="rt-card-verdict">
        {review.ai_verdict && <span className="rt-ai-badge">✨ AI Verdict</span>}
        <p className="rt-card-verdict-text">"{truncated}"</p>
        {shouldTruncate && (<button onClick={() => setExpanded(!expanded)} className="rt-read-more">{expanded ? 'Show less' : 'Read more'}</button>)}
      </div>
      {review.voice_review_url && (
        <button onClick={handlePlayVoice} className="rt-voice-play-btn" data-testid="rt-voice-play">
          {isPlaying ? '⏸️ Pause' : '🎙️ Listen'}
          <audio ref={audioRef} src={absoluteVoiceUrl(review.voice_review_url)} onEnded={() => setIsPlaying(false)} preload="none" />
        </button>
      )}
      <div className="rt-card-footer">
        <div className="rt-card-reviewer">
          <span className="rt-reviewer-avatar">{review.is_anonymous ? '🎭' : '👤'}</span>
          <div>
            <span className="rt-reviewer-name">{reviewerDisplay}</span>
            <span className="rt-reviewer-time">{timeAgo}</span>
          </div>
        </div>
        <div className="rt-card-actions">
          <ShareButton review={review} />
          <button onClick={handleFlag} className={`rt-flag-btn ${flagged ? 'rt-flagged' : ''}`} title={flagged ? 'Flagged' : 'Report this review'} disabled={flagged} data-testid="rt-flag-btn">🚩</button>
        </div>
      </div>
    </article>
  );
}
